import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getApiBase } from '../lib/apiBase';


const API = getApiBase();
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const parseResponse = async (res) => {
  try {
    const text = await res.text();
    try { return JSON.parse(text); } catch { return { _raw: text }; }
  } catch {
    return {};
  }
};

const getErrorMessage = (res, data, fallback) => {
  if (typeof data?.detail === 'string') return data.detail;
  if (Array.isArray(data?.detail)) return data.detail.map(d => d.msg || d).join(', ');
  if (data?._raw) return `Server error (${res.status}): ${data._raw.substring(0, 100)}`;
  return `${fallback} (status ${res.status})`;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('dd-token');
    if (!token) { setUser(false); setLoading(false); return; }
    try {
      const res = await fetch(`${API}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await parseResponse(res);
        if (data?.user) { setUser(data.user); } else { localStorage.removeItem('dd-token'); setUser(false); }
      } else {
        localStorage.removeItem('dd-token');
        setUser(false);
      }
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    let res;
    try {
      res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
    } catch (networkErr) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    let data;
    try {
      const text = await res.text();
      data = text ? JSON.parse(text) : {};
    } catch {
      data = {};
    }
    if (res.status === 403) {
      throw new Error(data?.detail || 'Please verify your email before signing in. Check your inbox for the verification link.');
    }
    if (!res.ok) throw new Error(data?.detail || `Invalid email or password`);
    if (!data.token) throw new Error('No token received from server');
    localStorage.setItem('dd-token', data.token);
    setUser(data.user);
    return data;
  };

  const register = async (name, email, password, company) => {
    let res;
    try {
      res = await fetch(`${API}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password, company }),
      });
    } catch (networkErr) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    let data;
    try {
      const text = await res.text();
      data = text ? JSON.parse(text) : {};
    } catch {
      data = {};
    }
    if (res.status === 400) {
      throw new Error(data?.detail || 'Email already registered');
    }
    if (!res.ok) throw new Error(data?.detail || `Registration failed (${res.status})`);
    if (data.requires_verification) {
      return { requires_verification: true, email: data.email };
    }
    if (data.token) {
      localStorage.setItem('dd-token', data.token);
      setUser(data.user);
    }
    return data;
  };

  const resendVerification = async (email) => {
    let res;
    try {
      res = await fetch(`${API}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
    } catch {
      throw new Error('Network error. Please try again.');
    }
    let data;
    try { const t = await res.text(); data = t ? JSON.parse(t) : {}; } catch { data = {}; }
    if (!res.ok) throw new Error(data?.detail || 'Failed to resend verification email');
    return data;
  };

  const requestPasswordReset = async (email) => {
    let res;
    try {
      res = await fetch(`${API}/api/auth/request-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
    } catch {
      throw new Error('Network error. Please try again.');
    }
    let data;
    try { const t = await res.text(); data = t ? JSON.parse(t) : {}; } catch { data = {}; }
    if (!res.ok) throw new Error(data?.detail || 'Request failed');
    return data;
  };

  const resetPassword = async (token, newPassword) => {
    const res = await fetch(`${API}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword }),
    });
    if (!res.ok) {
      let detail = '';
      try { const d = await res.json(); detail = d.detail || ''; } catch { /* proxy may strip body */ }
      if (detail) throw new Error(detail);
      if (res.status === 400) throw new Error('Invalid or expired reset token. Please request a new password reset link.');
      throw new Error(`Reset failed (status ${res.status})`);
    }
    return res.json();
  };

  const logout = async () => {
    const token = localStorage.getItem('dd-token');
    try {
      await fetch(`${API}/api/auth/logout`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
    } catch {}
    localStorage.removeItem('dd-token');
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, requestPasswordReset, resetPassword, resendVerification, refreshUser: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
